import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import Toast from '../actions/Toasts';
import apiService from '../../services/apiServices';
import templateValidationSchema from '../../services/templateValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import TemplateDesigner from './TemplateDesigner';

// Icons
import CheckIcon from '@mui/icons-material/Check';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CONTRACT_TEMPLATE_TAGS } from '../../config/contractTemplateTags';
import { nameFromFile } from '../../utils/templateImport';
import { useTemplateImport } from './ImportReviewDialog';
import WordTemplatePanel, { WordPreview } from './WordTemplatePanel';
import { analyzeWordFile, uploadTemplateDocx, fetchTemplateDocx, removeTemplateDocx } from '../../utils/wordTemplate';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}


/**
 * Krijo dhe Ndrysho template — një faqe e vetme. Me `:id` në URL ngarkon
 * shabllonin ekzistues (emrin, tekstin, dokumentin Word me fushat e lidhura)
 * dhe ruan me PUT; pa të, krijon një të ri.
 */
const AddTemplate = () => {
  const history = useHistory();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [loading, setLoading] = useState(isEdit);
  const alert = { open: false, message: '', variant: '' };
  const [toast, setToast] = useState(alert);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(templateValidationSchema),
    defaultValues: {
      Name: '',
      Content: '',
    }
  });

  // Fushat vendosen me setValue nga komponente të kontrolluara; pa regjistrim
  // `watch` nuk njoftohet dhe fusha e emrit e humbiste çdo shkronjë.
  useEffect(() => {
    register('Name');
    register('Content');
  }, [register]);

  // «Kopjo si bazë» nga lista: shablloni nis me tekstin e një tjetri, gati për ndryshim.
  const location = useLocation();
  useEffect(() => {
    const src = location.state?.copyFrom;
    if (!src?.html) return;
    setValue('Name', `${src.name || 'Shabllon'} (kopje)`);
    setValue('Content', src.html, { shouldValidate: true });
    setActiveTab(1);
    history.replace({ ...location, state: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const templateText = watch('Content');
  const templateName = watch('Name');

  // Shablloni Word: dokumenti ruhet me dizajnin e vet; gjenerimi plotëson fushat brenda tij.
  const [word, setWord] = useState(null);
  const [wordBusy, setWordBusy] = useState(false);
  // Te redaktimi: Word-i u hoq → fshihet edhe te serveri kur ruhet.
  const [wordRemoved, setWordRemoved] = useState(false);

  useEffect(() => {
    if (!isEdit) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await apiService().query('getTemplate', 'id=' + editId);
        const item = res?.data?.[0];
        if (!alive) return;
        if (!res?.success || !item) {
          setToast({ open: true, variant: 'error', message: 'Template nuk u gjet.' });
          setTimeout(() => history.push('/contracts-templates'), 1500);
          return;
        }
        setValue('Name', item.Name || '');
        setValue('Content', item.Content || '');
        if (item.HasDocx) {
          const file = await fetchTemplateDocx(editId, item.DocxName);
          if (file && alive) {
            const { tags } = await analyzeWordFile(file).catch(() => ({ tags: [] }));
            setWord({ file, tags, fieldMap: item.FieldMap || {} });
          }
        }
        setActiveTab(1);
      } catch {
        if (alive) setToast({ open: true, variant: 'error', message: 'Template nuk u ngarkua.' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);
  const startWord = async (file) => {
    if (!file) return;
    setWordBusy(true);
    try {
      const { html, tags, fieldMap } = await analyzeWordFile(file);
      setWord({ file, tags, fieldMap });
      setWordRemoved(false);
      setValue('Content', html, { shouldValidate: true });
      if (!templateName?.trim()) setValue('Name', nameFromFile(file.name));
      const linked = Object.keys(fieldMap).length;
      setToast({
        open: true,
        variant: tags.length && linked === tags.length ? 'success' : 'info',
        message: tags.length
          ? `Dokumenti u ngarkua me dizajnin e vet · ${linked}/${tags.length} fusha u lidhën vetë.`
          : 'Dokumenti u ngarkua, por s\'ka fusha si {!emri!}. Shtojini në Word që të plotësohen automatikisht.',
      });
      setActiveTab(1);
    } catch (e) {
      setToast({ open: true, variant: 'error', message: e?.message || 'Dokumenti nuk u lexua.' });
    } finally {
      setWordBusy(false);
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const payload = {
      Name: (data.Name || '').trim(),
      Content: data.Content || '',
      CreatedAt: new Date().toISOString().slice(0, 10),
    };

    try {
      const response = isEdit
        ? await apiService().put('contractsTemplates', { Id: Number(editId), Name: payload.Name, Content: payload.Content })
        : await apiService().post('contractsTemplates', payload);
      if (response.success && (word?.file || (isEdit && wordRemoved))) {
        const id = isEdit ? Number(editId) : (response.data?.Id ?? response.data?.id);
        try {
          if (word?.file) await uploadTemplateDocx(id, word.file, word.fieldMap);
          else await removeTemplateDocx(id);
        } catch (e) {
          setToast({ open: true, variant: 'error', message: `Template u ruajt, por dokumenti Word jo: ${e.message}` });
          setIsSubmitting(false);
          return;
        }
      }
      if (response.success) {
        setToast({
          open: true,
          message: isEdit ? 'Ndryshimet u ruajtën.' : 'Template u ruajt me sukses! Duke u ridrejtuar...',
          variant: 'success',
        });
        setTimeout(() => {
          history.push('/contracts-templates');
        }, 1500);
      } else {
        setToast({
          open: true,
          message: response.message || 'Ka ndodhur një gabim gjatë ruajtjes!',
          variant: 'error',
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      setToast({
        open: true,
        message: 'Ka ndodhur një gabim në lidhje me serverin!',
        variant: 'error',
      });
      setIsSubmitting(false);
    }
  };

  const getTemplateStats = () => {
    const text = templateText || '';
    const wordCount = text.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(w => w.length > 0).length;
    const tagCount = (text.match(/@\w+/g) || []).length;
    return { wordCount, tagCount };
  };

  const stats = word
    ? { ...getTemplateStats(), tagCount: Object.keys(word.fieldMap || {}).length }
    : getTemplateStats();
  const isFormValid = templateName && ((templateText && stats.wordCount > 0) || word?.file);

  // Sample data for preview
  const SAMPLE_DATA = {
    FirstName: 'Arben',
    LastName: 'Shala',
    ParentName: 'Ilir',
    PersonalId: '123456789',
    Address: 'Rr. Prishtina 10',
    Phone: '+383 44 000 000',
    PostalCode: '10000',
    BankAccount: 'XK051234567890',
    BankName: 'Banka Demo',
    Birthday: '10/05/1990',
    Country: 'Kosovë',
    Place: 'Prishtinë',
    Email: 'arben.shala@example.com',
    Gender: 'Mashkull',
    MaritalStatus: 'Beqar',
    Qualification: 'Bachelor',
    Profession: 'Matematikan',
    ContractType: 'Kohë e caktuar',
    StartDate: '01/01/2025',
    EndDate: '31/12/2025',
    WorkType: 'Full-time',
    SalaryType: 'Mujore',
    Department: 'IT',
    BaseSalary: '600€',
    BrutoNeto: 'Bruto',
    HourSalary: '3.5€',
    HourSalaryWeekend: '4.5€',
    HourSalaryHoliday: '5.0€',
    TermsOfContract: 'Sipas ligjit të punës',
  };

  const replaceTokens = (html) => {
    if (!html) return '';
    let output = html;
    for (const [key, value] of Object.entries(SAMPLE_DATA)) {
      const regex = new RegExp('@' + key + '(?![A-Za-z0-9_])', 'g');
      output = output.replace(regex, value || '');
    }
    return output;
  };

  const previewHtml = replaceTokens(templateText);

  const wordImport = useTemplateImport({
    onApplied: (html, { fields, blanks }, fileName) => {
      setValue('Content', html, { shouldValidate: true });
      if (!templateName?.trim()) setValue('Name', nameFromFile(fileName));
      setToast({
        open: true,
        variant: 'success',
        message: `Dokumenti u importua me ${fields} fusha dinamike${blanks ? ` · ${blanks} vende mbetën për plotësim me dorë` : ''}.`,
      });
      setActiveTab(1);
    },
    onError: (message) => setToast({ open: true, message, variant: 'error' }),
  });

  const goBack = () => history.push('/contracts-templates');
  const saveButton = (
    <Tooltip title={isFormValid ? '' : 'Plotëso emrin dhe përmbajtjen para ruajtjes'}>
      <span>
        <Button
          variant="contained"
          disableElevation
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || !isFormValid}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isSubmitting ? 'Duke ruajtur…' : 'Ruaj template'}
        </Button>
      </span>
    </Tooltip>
  );

  const STEPS = [
    { label: 'Të dhënat', done: Boolean(templateName?.trim()) },
    { label: 'Përmbajtja', done: stats.wordCount > 0 },
    { label: 'Parapamja', done: false },
  ];
  const canNext = activeTab === 0 ? Boolean(templateName?.trim()) : (stats.wordCount > 0 || Boolean(word?.file));

  return (
    <Box sx={{ pb: 4 }}>
      <Toast {...toast} />
      {wordImport.dialog}

      {/* Header — një vend i vetëm për Anulo/Ruaj; hapat kanë vetëm navigimin. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <IconButton size="small" onClick={goBack} aria-label="Kthehu te template-t"
            sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.2, fontSize: { xs: '1.05rem', md: '1.25rem' } }} noWrap>
              {templateName?.trim() || (isEdit ? 'Ndrysho template' : 'Template i ri')}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {isEdit
                ? (word ? 'Shabllon Word · dizajni ruhet si në dokument' : 'Ndrysho tekstin ose ngarko një dokument Word')
                : 'Template kontrate me fusha që plotësohen automatikisht për çdo punëtor'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexShrink: 0 }}>
          <Button onClick={goBack} disabled={isSubmitting} sx={{ textTransform: 'none', color: 'text.secondary' }}>Anulo</Button>
          {saveButton}
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        {/* Hapat — tabs-at janë vetë stepper-i, jo një kopje e dytë e tij. */}
        <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider', pr: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{ flex: 1, minHeight: 48, px: 1, '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600, fontSize: 14 } }}
          >
            {STEPS.map((st, i) => (
              <Tab
                key={st.label}
                disabled={loading || (i > 0 && !templateName?.trim() && !stats.wordCount)}
                iconPosition="start"
                icon={(
                  <Box sx={{
                    width: 20, height: 20, borderRadius: '50%', fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: st.done ? 'success.main' : activeTab === i ? 'primary.main' : 'action.selected',
                    color: st.done || activeTab === i ? 'common.white' : 'text.secondary',
                  }}>
                    {st.done ? <CheckIcon sx={{ fontSize: 13 }} /> : i + 1}
                  </Box>
                )}
                label={st.label}
              />
            ))}
          </Tabs>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}>
            {stats.wordCount} fjalë · {stats.tagCount} fusha dinamike
          </Typography>
        </Stack>

        {loading && <LinearProgress />}
        <CardContent sx={{ p: { xs: 2, md: 3 }, opacity: loading ? 0.4 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          {/* Hapi 1: Të dhënat */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ maxWidth: 720 }}>
              <TextField
                fullWidth
                autoFocus
                required
                label="Emri i template-it"
                placeholder="p.sh. Kontrata e Punës – Full Time"
                value={templateName}
                onChange={(e) => setValue('Name', e.target.value, { shouldValidate: Boolean(errors.Name) })}
                error={!!errors.Name}
                helperText={errors.Name?.message || 'Ky emër shfaqet kur zgjidhni template-in për një kontratë të re.'}
                disabled={isSubmitting}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                {isEdit ? 'Si doni ta ndryshoni?' : 'Si doni të filloni?'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {/* Formati i vet i përdoruesit është rruga kryesore: pranohet me
                    klikim ose duke e tërhequr dokumentin, pa pritur emrin — emri
                    merret nga skedari kur mungon. */}
                <StartOption
                  component="label"
                  active={dragOver}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); startWord(e.dataTransfer.files?.[0]); }}
                  icon={wordBusy ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon fontSize="small" />}
                  title={wordBusy ? 'Duke lexuar dokumentin…' : (word ? 'Zëvendëso dokumentin Word' : 'Ngarko kontratën tuaj nga Word')}
                  desc="Dizajni mbetet si në Word. Fushat si {!emri!} ose {{emri}} lidhen vetë — ju vetëm i kontrolloni."
                >
                  <input type="file" hidden accept=".docx"
                    onChange={(e) => { startWord(e.target.files?.[0]); e.target.value = ''; }} />
                </StartOption>
                <StartOption
                  disabled={!templateName?.trim()}
                  onClick={() => setActiveTab(1)}
                  icon={<EditNoteIcon fontSize="small" />}
                  title={isEdit ? (word ? 'Shiko fushat e dokumentit' : 'Redakto tekstin') : 'Fillo nga e para'}
                  desc={isEdit && word
                    ? 'Kontrolloni cila fushë e dokumentit lidhet me cilën të dhënë të punëtorit.'
                    : 'Shkruani tekstin në editor dhe futni fushat dinamike me një klik.'}
                />
              </Box>
              {!templateName?.trim() && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Për të filluar nga e para, shkruani fillimisht emrin e template-it.
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Doni ta redaktoni tekstin këtu në sistem?{' '}
                <Box component="label" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}>
                  Importoje Word-in si tekst të redaktueshëm
                  <input type="file" hidden accept=".docx"
                    onChange={(e) => { setWord(null); wordImport.start(e.target.files?.[0]); e.target.value = ''; }} />
                </Box>
                {' '}(formatimi i thjeshtë ruhet, dizajni i plotë jo).
              </Typography>
            </Box>
          </TabPanel>

          {/* Hapi 2: Përmbajtja */}
          <TabPanel value={activeTab} index={1}>
            {errors.Content && (
              <Alert severity="error" sx={{ mb: 2 }}>{errors.Content.message}</Alert>
            )}
            <Box sx={{ opacity: isSubmitting ? 0.6 : 1, pointerEvents: isSubmitting ? 'none' : 'auto', transition: 'opacity .3s' }}>
              {word ? (
                <WordTemplatePanel
                  word={word}
                  busy={wordBusy}
                  onFieldChange={(key, value) => setWord((w) => {
                    const fieldMap = { ...w.fieldMap };
                    if (value) fieldMap[key] = value; else delete fieldMap[key];
                    return { ...w, fieldMap };
                  })}
                  onReplace={startWord}
                  onRemove={() => { setWord(null); setWordRemoved(isEdit); }}
                />
              ) : (
                <TemplateDesigner
                  value={templateText}
                  onChange={(text) => setValue('Content', text, { shouldValidate: true })}
                  tags={CONTRACT_TEMPLATE_TAGS}
                />
              )}
            </Box>
          </TabPanel>

          {/* Hapi 3: Parapamja */}
          <TabPanel value={activeTab} index={2}>
            {word ? (<>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Dokumenti ashtu siç do të dalë. Fushat ({Object.keys(word.fieldMap || {}).length}) plotësohen me të dhënat e punëtorit kur gjenerohet kontrata — dizajni mbetet ky.
              </Typography>
              <WordPreview file={word.file} />
            </>) : (<>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Kështu duket kontrata e gjeneruar. Fushat dinamike janë zëvendësuar me të dhëna shembull.
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: { xs: 1.5, md: 3 } }}>
              <Box sx={{
                maxWidth: 820, mx: 'auto', bgcolor: 'common.white', color: 'grey.900',
                p: { xs: 2.5, md: 6 }, minHeight: 480, borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,.08), 0 8px 24px rgba(0,0,0,.06)',
                overflowWrap: 'anywhere',
              }}>
                <Box
                  dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#9CA3AF;font-style:italic">Asnjë përmbajtje ende…</p>' }}
                  sx={{
                    fontSize: 14, lineHeight: 1.7,
                    '& p': { mb: 1.25 },
                    '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 2, mb: 1.5 },
                    '& h2': { fontSize: '1.25rem', fontWeight: 700, mt: 2, mb: 1 },
                    '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 1.5, mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 1.5 },
                    '& li': { mb: 0.5 },
                    '& table': { width: '100%', borderCollapse: 'collapse' },
                    '& img': { maxWidth: '100%' },
                  }}
                />
              </Box>
            </Box>
            </>)}
          </TabPanel>
        </CardContent>

        {/* Navigimi mes hapave */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}
          sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          {activeTab > 0 ? (
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveTab(activeTab - 1)} disabled={isSubmitting}
              sx={{ textTransform: 'none' }}>
              Mbrapa
            </Button>
          ) : <span />}
          {activeTab < 2 ? (
            <Button variant="contained" disableElevation endIcon={<ArrowForwardIcon />}
              onClick={() => setActiveTab(activeTab + 1)} disabled={!canNext}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {activeTab === 0 ? 'Vazhdo te përmbajtja' : 'Shiko parapamjen'}
            </Button>
          ) : saveButton}
        </Stack>
      </Card>
    </Box>
  );
};

function StartOption({ icon, title, desc, disabled, active, children, ...rest }) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      {...rest}
      sx={{
        display: 'flex', gap: 1.5, p: 2, borderRadius: 2, border: 1,
        borderStyle: active ? 'dashed' : 'solid',
        borderColor: active ? 'primary.main' : 'divider',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto', bgcolor: active ? 'action.hover' : 'background.paper',
        transition: 'border-color .15s, background-color .15s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5, lineHeight: 1.5 }}>{desc}</Typography>
      </Box>
      {children}
    </Box>
  );
}

export default AddTemplate;
